import fetchJson from "@/lib/fetchJson";
import useSWR, { type SWRConfiguration } from "swr";

/**
 * Instagram Messaging integration - frontend service helpers.
 *
 * Mirrors the shape of `services/engagement.ts`: SWR for reads,
 * plain fetch helpers for mutations. Real-time updates rely on SWR's
 * polling (`refreshInterval`) - there's no websocket on the server today.
 */

export type InstagramAccount = {
  id: string;
  igUserId: string;
  igUsername: string | null;
  provider?: "meta" | "zernio" | string;
  tokenExpiresAt: string | null;
  createdAt: string;
  lastRefreshedAt: string | null;
};

export type InstagramConversation = {
  id: string;
  participantIgScopedId: string;
  participantUsername: string | null;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  createdAt: string;
};

export type InstagramMessage = {
  id: string;
  mid: string;
  direction: "inbound" | "outbound";
  text: string | null;
  attachmentsJson: unknown | null;
  senderIgScopedId: string;
  timestamp: string;
  isHumanAgentReply: boolean;
  sentByUserId: string | null;
  errorMessage: string | null;
};

export type InstagramMessagesResponse = {
  messages: InstagramMessage[];
  conversation: {
    id: string;
    participantIgScopedId: string;
    participantUsername: string | null;
    lastInboundAt: string | null;
    insideStandardWindow: boolean;
  };
};

/** Path segment after `/backend` (or dev proxy prefix). */
const INSTAGRAM_AUTH_START_PATH = "/integrations/instagram/auth/start";

/** @deprecated Prefer {@link getInstagramAuthStartHref} for browser redirects. */
export const instagramAuthStartUrl = `/backend${INSTAGRAM_AUTH_START_PATH}`;

/**
 * Full same-origin URL for OAuth. Must be a top-level navigation so session
 * cookies are sent; mirrors `fetchJson` dev vs prod routing.
 */
export function getInstagramAuthStartHref(): string {
  if (typeof window === "undefined") {
    return instagramAuthStartUrl;
  }
  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_PROXY !== "no"
  ) {
    return `${window.location.origin}/api/proxy/backend${INSTAGRAM_AUTH_START_PATH}`;
  }
  return `${window.location.origin}/backend${INSTAGRAM_AUTH_START_PATH}`;
}

export const useInstagramAccounts = (enabled = true) =>
  useSWR<InstagramAccount[]>(
    enabled ? "/integrations/instagram/accounts" : null,
    fetchJson,
  );

export const useInstagramConversations = (
  igAccountId: string | undefined,
  swrOptions?: SWRConfiguration,
) =>
  useSWR<InstagramConversation[]>(
    igAccountId
      ? `/integrations/instagram/conversations?igAccountId=${encodeURIComponent(igAccountId)}`
      : null,
    fetchJson,
    swrOptions,
  );

export const useInstagramMessages = (
  conversationId: string | undefined,
  swrOptions?: SWRConfiguration,
) =>
  useSWR<InstagramMessagesResponse>(
    conversationId
      ? `/integrations/instagram/conversations/${encodeURIComponent(conversationId)}/messages`
      : null,
    fetchJson,
    swrOptions,
  );

export const sendInstagramMessage = (input: {
  igAccountId: string;
  recipientIgScopedId: string;
  text: string;
  forceHumanAgentTag?: boolean;
}) =>
  fetchJson("/integrations/instagram/messages/send", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const disconnectInstagramAccount = (igAccountId: string) =>
  fetchJson("/integrations/instagram/disconnect", {
    method: "POST",
    body: JSON.stringify({ igAccountId }),
  });
